import { useState, useEffect, useRef } from 'react';
import {
  Card,
  Group,
  Text,
  Box,
  SimpleGrid,
  ActionIcon,
  TextInput,
  Textarea,
  Button,
  Stack,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, Edit } from 'react-feather';
import { coachesApi } from '../../../services/api/coaches';
import { getApiErrorMessage } from '../../../services/api/client';
import type { Coach, UpdateCoachRequest } from '../../../types';
import {
  COACH_EMAIL_MESSAGES,
  coachEmailErrorField,
  isMedleyEmail,
  MEDLEY_EMAIL_LABEL,
  medleyEmailOf,
  normalizeEmailForCompare,
  PERSONAL_EMAIL_LABEL,
  validateMedleyEmail,
  validatePersonalEmail,
} from '../../../utils/medleyEmail';
import { CoachEmailFields } from '../../../components/coaches/CoachEmailFields';
import classes from '../CoachDetailPage.module.css';

// MED-1355: style guide greys. "Not set" uses the tertiary grey, so it
// does not read as a value. Notes use the secondary grey.
const NOT_SET_COLOR = 'var(--medley-text-tertiary)';
const NOTE_COLOR = 'var(--medley-text-secondary)';

interface BasicInfoSectionProps {
  coach: Coach;
  onSaved: (coach: Coach) => void;
  /**
   * MED-1355: a counter. Each increase opens edit mode, scrolls to this
   * card and focuses Medley Email. The page header badge uses it.
   */
  editMedleyEmailRequest?: number;
}

export function BasicInfoSection({
  coach,
  onSaved,
  editMedleyEmailRequest = 0,
}: BasicInfoSectionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [focusMedleyEmail, setFocusMedleyEmail] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const medleyInputRef = useRef<HTMLInputElement>(null);

  // A request made before this card mounted is not acted on again.
  const handledRequest = useRef(editMedleyEmailRequest);
  useEffect(() => {
    if (editMedleyEmailRequest > handledRequest.current) {
      handledRequest.current = editMedleyEmailRequest;
      setIsEditing(true);
      setFocusMedleyEmail(true);
    }
  }, [editMedleyEmailRequest]);

  // Runs after the edit form renders, so the Medley Email input exists.
  useEffect(() => {
    if (isEditing && focusMedleyEmail) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      medleyInputRef.current?.focus({ preventScroll: true });
      setFocusMedleyEmail(false);
    }
  }, [isEditing, focusMedleyEmail]);

  const fullName =
    [coach.first_name, coach.last_name].filter(Boolean).join(' ') || 'Unnamed Coach';

  // MED-1355: an older account can sign in with a non-Medley address in
  // users.email. That address is not a Medley email, so the card shows
  // "Not set" and the form starts empty. An empty field then leaves the
  // login address alone, because the payload omits an unchanged email.
  const storedMedleyEmail = medleyEmailOf(coach.email) ?? '';
  const loginOnlyEmail = coach.email && !storedMedleyEmail ? coach.email : null;
  // MED-1355 QA round 5: older data can hold a Medley address in the
  // personal email. The card and the form flag it. The form does not block
  // other edits for it: an unchanged personal email is not sent. A changed
  // value must pass the personal email rule, and the API refuses a Medley
  // address in the personal email on every save that sends it.
  const storedPersonalIsMedley = isMedleyEmail(coach.preferred_email);

  const form = useForm<UpdateCoachRequest>({
    // MED-1355: check each email field when it loses focus.
    validateInputOnBlur: ['preferred_email', 'email'],
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      preferred_email: '',
      coaching_specializations: '',
    },
    validate: {
      first_name: (value) => (value && value.trim() ? null : 'First name is required'),
      last_name: (value) => (value && value.trim() ? null : 'Last name is required'),
      // MED-1355: Coaching Specializations is optional, as in Add Coach and
      // the API. An empty value clears it.
      // MED-1355: same domain rules as the API. An unchanged Medley email is
      // not checked, so an older non-Medley value does not block other edits.
      email: (value) =>
        (value ?? '').trim() === storedMedleyEmail ? null : validateMedleyEmail(value),
      // An unchanged stored personal email is not checked, as the API does.
      // An older Medley address there then does not block other edits.
      preferred_email: (value) => {
        const stored = normalizeEmailForCompare(coach.preferred_email);
        return stored && normalizeEmailForCompare(value) === stored
          ? null
          : validatePersonalEmail(value);
      },
    },
  });

  // Reset form when entering edit mode
  useEffect(() => {
    if (isEditing) {
      form.setValues({
        first_name: coach.first_name || '',
        last_name: coach.last_name || '',
        email: storedMedleyEmail,
        phone: coach.phone || '',
        preferred_email: coach.preferred_email || '',
        coaching_specializations: coach.coaching_specializations || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable, only re-run when edit mode activates
  }, [isEditing, coach]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    form.reset();
    setIsEditing(false);
  };

  const handleSubmit = async (values: UpdateCoachRequest) => {
    try {
      setSaving(true);
      // MED-1355: send the Medley email only when it changed. Empty clears it.
      const { email, ...rest } = values;
      const nextEmail = (email ?? '').trim();
      const payload: UpdateCoachRequest =
        nextEmail === storedMedleyEmail ? rest : { ...rest, email: nextEmail || null };
      // QA round 5: send the personal email only when it changed, as the
      // coach portal does. An older Medley address there then does not
      // block a name or phone edit.
      const storedPersonal = normalizeEmailForCompare(coach.preferred_email);
      if (storedPersonal && normalizeEmailForCompare(payload.preferred_email) === storedPersonal) {
        delete payload.preferred_email;
      }
      const updatedCoach = await coachesApi.update(coach.id, payload);
      notifications.show({
        title: 'Success',
        message: 'Basic information updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      setIsEditing(false);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to update basic information');
      const field = coachEmailErrorField(error);
      if (field) {
        form.setFieldError(field, message);
        return;
      }
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  // MED-1355: a new Medley email also becomes the sign-in address. Say so
  // before the save, for a non-Medley login and for a changed Medley email.
  // Show the note only for a complete, valid Medley email, so a partial
  // value never gets a false message. Clearing a stored Medley email also
  // changes the sign-in, so that case gets its own note.
  // The note goes away while the Medley email field shows an error (for
  // example the API refusal to clear the email of a group lead or a 1:1
  // coach), so
  // the page never shows a note and an error that disagree.
  const medleyValue = (form.values.email ?? '').trim();
  const coachName = [coach.first_name?.trim(), coach.last_name?.trim()].filter(Boolean).join(' ');
  const medleyFieldHasError = Boolean(form.errors.email);
  const isNewValidMedleyEmail =
    medleyValue !== '' &&
    validateMedleyEmail(medleyValue) === null &&
    normalizeEmailForCompare(medleyValue) !== normalizeEmailForCompare(coach.email);
  const isClearingMedleyEmail = medleyValue === '' && storedMedleyEmail !== '';
  const noteAllowed = isEditing && !medleyFieldHasError;
  let signInNote: string | null = null;
  if (noteAllowed && isNewValidMedleyEmail) {
    signInNote = coach.email
      ? `${coachName || 'This coach'} signs in with ${coach.email} today. After you save, ${coachName || 'the coach'} signs in with this Medley email.`
      : `After you save, ${coachName || 'the coach'} can sign in with this Medley email.`;
  } else if (noteAllowed && isClearingMedleyEmail) {
    signInNote = `${coachName || 'This coach'} signs in with ${storedMedleyEmail} today. If you remove it, ${coachName || 'the coach'} can sign in only with the personal email.`;
  }
  const signInChanges = signInNote !== null;

  // View mode
  if (!isEditing) {
    return (
      <Card ref={cardRef} className={classes.profileCard}>
        <Group justify="space-between" mb="md">
          <Text className={classes.cardTitle}>Basic Information</Text>
          <ActionIcon
            variant="subtle"
            onClick={handleEdit}
            aria-label="Edit basic information"
          >
            <Edit size={16} />
          </ActionIcon>
        </Group>
        {/* MED-1355: Personal Email, then Medley Email, side by side. The
            same order as the edit form and the coach portal. */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Box>
            <Text className={classes.fieldLabel}>Full Name</Text>
            <Text className={classes.fieldContent}>{fullName}</Text>
          </Box>
          <Box>
            <Text className={classes.fieldLabel}>Phone Number</Text>
            {coach.phone ? (
              <Text className={classes.fieldContent}>{coach.phone}</Text>
            ) : (
              <Text className={classes.fieldContent} c={NOT_SET_COLOR}>Not set</Text>
            )}
          </Box>
          <Box>
            <Text className={classes.fieldLabel}>{PERSONAL_EMAIL_LABEL}</Text>
            {coach.preferred_email ? (
              <Text className={classes.fieldContent}>{coach.preferred_email}</Text>
            ) : (
              <Text className={classes.fieldContent} c={NOT_SET_COLOR}>Not set</Text>
            )}
            {storedPersonalIsMedley && (
              <Text size="xs" fw={500} c="var(--medley-text-warning)" mt={2}>
                {COACH_EMAIL_MESSAGES.storedPersonalIsMedley}
              </Text>
            )}
          </Box>
          <Box>
            <Text className={classes.fieldLabel}>{MEDLEY_EMAIL_LABEL}</Text>
            {storedMedleyEmail ? (
              <Text className={classes.fieldContent}>{storedMedleyEmail}</Text>
            ) : (
              <Text className={classes.fieldContent} c={NOT_SET_COLOR}>Not set</Text>
            )}
            {loginOnlyEmail && (
              <Text size="xs" c={NOTE_COLOR} mt={2}>
                Signs in with {loginOnlyEmail}, which is not a Medley address. A Medley email saved here replaces it as the sign-in address.
              </Text>
            )}
          </Box>
          <Box style={{ gridColumn: '1 / -1' }}>
            <Text className={classes.fieldLabel}>Coaching Specializations</Text>
            {coach.coaching_specializations ? (
              <Text className={classes.fieldContent}>{coach.coaching_specializations}</Text>
            ) : (
              <Text className={classes.fieldContent} c={NOT_SET_COLOR}>Not set</Text>
            )}
          </Box>
          {coach.bio && (
            <Box style={{ gridColumn: '1 / -1' }}>
              <Text className={classes.fieldLabel}>Bio</Text>
              <Text className={classes.fieldContent}>{coach.bio}</Text>
            </Box>
          )}
        </SimpleGrid>
      </Card>
    );
  }

  // Edit mode
  return (
    <Card ref={cardRef} className={classes.profileCard}>
      <Text className={classes.cardTitle} mb="md">Basic Information</Text>
      {/* noValidate: the form rules show each error under its field, never
          the browser's own bubble. */}
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Box>
              <Text className={classes.fieldLabel}>First Name</Text>
              <TextInput
                placeholder="Enter first name"
                {...form.getInputProps('first_name')}
              />
            </Box>
            <Box>
              <Text className={classes.fieldLabel}>Last Name</Text>
              <TextInput
                placeholder="Enter last name"
                {...form.getInputProps('last_name')}
              />
            </Box>
          </SimpleGrid>

          {/* MED-1355: the same email fields, order and required mark as
              Add Coach. Each field keeps a fixed slot for its error, so the
              Save Changes button does not move when an error comes or goes. */}
          <Box>
            <CoachEmailFields
              personalInputProps={form.getInputProps('preferred_email')}
              medleyInputProps={form.getInputProps('email')}
              labelClassName={classes.fieldLabel}
              medleyInputRef={medleyInputRef}
              personalWarning={
                isMedleyEmail(form.values.preferred_email)
                  ? COACH_EMAIL_MESSAGES.storedPersonalIsMedley
                  : undefined
              }
            />

          </Box>

          <Box>
            <Text className={classes.fieldLabel}>Phone Number</Text>
            <TextInput placeholder="Optional" {...form.getInputProps('phone')} />
          </Box>

          <Box>
            <Text className={classes.fieldLabel}>Coaching Specializations</Text>
            <Textarea
              placeholder="Enter coaching specializations"
              minRows={3}
              {...form.getInputProps('coaching_specializations')}
            />
          </Box>

          {/* MED-1355: the sign-in note sits below the buttons. It tells
              what the save does, so it belongs next to Save Changes. When it
              comes or goes, only the space below the buttons changes, so the
              buttons do not move. A fixed slot above the buttons would leave
              an empty gap in normal use. One block, so the empty live region
              adds no Stack gap. */}
          <Box mt="md">
            <Group justify="flex-end">
              <Button variant="default" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" loading={saving}>
                Save Changes
              </Button>
            </Group>

            {/* The live region is always in the page, so a screen reader reads
                the note when it appears. */}
            <Box role="status" aria-live="polite">
              {signInChanges && (
                <Group
                  gap={8}
                  wrap="nowrap"
                  align="flex-start"
                  mt={12}
                  style={{
                    backgroundColor: 'var(--medley-bg-warning)',
                    color: 'var(--medley-text-warning)',
                    borderRadius: 6,
                    padding: '8px 12px',
                  }}
                >
                  <AlertTriangle size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Text size="xs" fw={500} c="inherit" style={{ overflowWrap: 'anywhere' }}>
                    {signInNote}
                  </Text>
                </Group>
              )}
            </Box>
          </Box>
        </Stack>
      </form>
    </Card>
  );
}
